import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { LIBRARY_API_BASE, LIBRARY_CLIENT_HEADER, isLibraryUrl } from './library-api';
import { LibraryAuthService } from './library-auth.service';

/** Calls whose 401 is an expected answer, not an expired session. */
function isAuthEntryPoint(url: string): boolean {
  return (
    url === `${LIBRARY_API_BASE}/me` ||
    url === `${LIBRARY_API_BASE}/auth/login` ||
    url === `${LIBRARY_API_BASE}/auth/logout`
  );
}

/**
 * Job Story Library requests only (`/api/library/*`): sends the session cookie (`withCredentials`) and the
 * `X-Story-Client` header the server requires for CSRF protection, and — when a call comes back 401 because the
 * session ended — signs the person out locally and returns them to the library sign-in. Every other request is
 * passed through untouched, and the Admin Portal's own interceptors skip library URLs (see `auth.interceptor.ts`).
 */
export const libraryInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isLibraryUrl(req.url)) return next(req);

  const auth = inject(LibraryAuthService);
  const router = inject(Router);

  const request = req.clone({
    withCredentials: true,
    setHeaders: { [LIBRARY_CLIENT_HEADER]: 'web' },
  });

  return next(request).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401 && !isAuthEntryPoint(req.url)) {
        const returnUrl = router.url;
        auth.clear();
        if (!returnUrl.startsWith('/library/sign-in')) {
          void router.navigate(['/library/sign-in'], { queryParams: { returnUrl, expired: 1 } });
        }
      }
      return throwError(() => error);
    }),
  );
};
